// external imports
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircleOutline,
  HighlightOff,
  WarningAmberRounded,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import {
  StatusPill,
  ApprovalProgress,
  FileTypeBadge,
  formatBytes,
  formatDate,
  colorForName,
  getInitials,
  parseTaskError,
  downloadFile,
} from './common';

const TaskDetail = () => {
  const { taskId } = useParams();
  const apiCaller = useApiCaller();
  const navigate = useNavigate();

  const [task, setTask] = React.useState(null);
  const [assignments, setAssignments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [unauthorized, setUnauthorized] = React.useState(false);

  // review state
  const [reviewingId, setReviewingId] = React.useState(null); // assignment _id in flight
  const [rejectTarget, setRejectTarget] = React.useState(null); // assignment being rejected
  const [remark, setRemark] = React.useState('');
  const [rejectError, setRejectError] = React.useState('');

  // cancel state
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  const fetchTask = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUnauthorized(false);
      const response = await apiCaller.get(`/task/${taskId}`);
      setTask(response?.data?.data?.task ?? null);
      setAssignments(response?.data?.data?.assignments ?? []);
    } catch (err) {
      const parsed = parseTaskError(err, 'Failed to load task. Please try again.');
      setError(parsed.message);
      setUnauthorized(parsed.unauthorized);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  React.useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  // POST /task/assignment/:id/review — approve or reject a submitted language
  const submitReview = async (assignment, action, reviewRemark) => {
    try {
      setReviewingId(assignment._id);
      const body = action === 'reject' ? { action, remark: reviewRemark } : { action };
      const response = await apiCaller.post(`/task/assignment/${assignment._id}/review`, body);
      const nextStatus = response?.data?.data?.taskStatus;

      // reflect the header pill immediately (esp. auto-flip to "completed")
      if (nextStatus) setTask((prev) => (prev ? { ...prev, status: nextStatus } : prev));

      if (action === 'approve') {
        toast.success(
          nextStatus === 'completed'
            ? 'Last language approved — task completed.'
            : `${assignment.languageName} approved.`
        );
      } else {
        toast.success(`${assignment.languageName} sent back to the translator.`);
      }

      setRejectTarget(null);
      setRemark('');
      setRejectError('');
      await fetchTask();
    } catch (err) {
      const message = parseTaskError(err, 'Failed to submit review. Please try again.').message;
      if (action === 'reject') {
        setRejectError(message); // keep the modal open so they can fix the remark
      } else {
        toast.error(message);
      }
    } finally {
      setReviewingId(null);
    }
  };

  const handleApprove = (assignment) => submitReview(assignment, 'approve');

  const handleRejectConfirm = () => {
    if (!remark.trim()) {
      setRejectError('A remark is required when rejecting.');
      return;
    }
    submitReview(rejectTarget, 'reject', remark.trim());
  };

  // POST /task/:taskId/cancel — terminal, creator-only
  const handleCancel = async () => {
    try {
      setCancelling(true);
      const response = await apiCaller.post(`/task/${taskId}/cancel`);
      const nextStatus = response?.data?.data?.task?.status ?? 'cancelled';
      setTask((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      toast.success('Task cancelled.');
      setCancelOpen(false);
      await fetchTask();
    } catch (err) {
      toast.error(parseTaskError(err, 'Failed to cancel task. Please try again.').message);
    } finally {
      setCancelling(false);
    }
  };

  // overall approval progress across all language assignments
  const progress = React.useMemo(() => {
    const total = assignments.length;
    const approved = assignments.filter((a) => a.status === 'approved').length;
    return { total, approved };
  }, [assignments]);

  const sourceFiles = task?.attachments ?? task?.sourceFiles ?? [];
  const canCancel = task?.status === 'active'; // completed/cancelled are terminal

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        <Button
          onClick={() => navigate('/tasks')}
          startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            mb: 1.5,
            '&:hover': { background: 'transparent', color: '#334155' },
          }}
        >
          Back to tasks
        </Button>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{ fontWeight: 700, color: unauthorized ? '#b45309' : '#b91c1c', mb: 0.5 }}
            >
              {unauthorized ? 'Not authorized' : 'Something went wrong'}
            </Typography>
            <Typography sx={{ color: unauthorized ? '#92400e' : '#dc2626', fontSize: 14 }}>
              {error}
            </Typography>
          </Box>
        ) : (
          <>
            {/* header card */}
            <Box
              sx={{
                background: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                p: { xs: 2.5, md: 3 },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                    <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b' }}>
                      {task?.taskName}
                    </Typography>
                    <StatusPill status={task?.status} />
                  </Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: 13, mt: 0.5 }}>
                    Created {formatDate(task?.createdAt)}
                  </Typography>
                  {task?.taskDescription && (
                    <Typography sx={{ color: '#64748b', fontSize: 14, mt: 1, maxWidth: 720 }}>
                      {task.taskDescription}
                    </Typography>
                  )}
                </Box>

                {/* {canCancel && (
                  <Button
                    onClick={() => setCancelOpen(true)}
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                      background: '#dc2626',
                      boxShadow: 'none',
                      '&:hover': { background: '#b91c1c' },
                    }}
                  >
                    Cancel Task
                  </Button>
                )} */}
              </Box>

              {/* overall review progress */}
              <Box sx={{ mt: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                    Overall review progress
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                    {progress.approved} / {progress.total} approved
                  </Typography>
                </Box>
                <ApprovalProgress
                  approved={progress.approved}
                  total={progress.total}
                  showLabel={false}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'flex-start',
              }}
            >
              {/* Source files */}
              <Box
                sx={{
                  width: { xs: '100%', md: 300 },
                  flexShrink: 0,
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  p: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: '#94a3b8',
                    mb: 1.5,
                  }}
                >
                  SOURCE FILE{sourceFiles.length > 1 ? 'S' : ''}
                </Typography>
                {sourceFiles.length === 0 && (
                  <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No source files.</Typography>
                )}
                {sourceFiles.map((file) => (
                  <Box
                    key={file.fileKey}
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      p: 1.5,
                      mb: 1.5,
                      '&:last-of-type': { mb: 0 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <FileTypeBadge fileType={file.fileType} fileName={file.fileName} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.fileName}
                        </Typography>
                        {file.fileSize != null && (
                          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                            {formatBytes(file.fileSize)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {file.signedUrl && (
                      <Button
                        onClick={() => downloadFile(file.signedUrl, file.fileName)}
                        fullWidth
                        size="small"
                        sx={{
                          mt: 1.25,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          background: '#f1f5f9',
                          color: '#334155',
                          '&:hover': { background: '#e2e8f0' },
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Per-language assignments */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  p: { xs: 2, md: 2.5 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: '#94a3b8',
                    mb: 1.5,
                  }}
                >
                  PER-LANGUAGE ASSIGNMENTS
                </Typography>

                {assignments.length === 0 && (
                  <Typography sx={{ color: '#94a3b8', fontSize: 14, py: 2 }}>
                    No language assignments on this task.
                  </Typography>
                )}

                {assignments.map((a) => {
                  const submission = a.submission || {};
                  const hasFile = !!submission.signedUrl || !!submission.fileName;
                  const isSubmitted = a.status === 'submitted';
                  const rowBusy = reviewingId === a._id;
                  return (
                    <Box
                      key={a.languageCode}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        border: '1px solid #eef2f7',
                        borderRadius: '10px',
                        p: 1.5,
                        mb: 1.25,
                        '&:last-of-type': { mb: 0 },
                      }}
                    >
                      {/* language chip */}
                      <Box
                        sx={{
                          minWidth: 88,
                          textAlign: 'center',
                          background: '#f1f5f9',
                          borderRadius: '999px',
                          px: 1.5,
                          py: 0.75,
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: '#475569',
                        }}
                      >
                        {a.languageName}
                      </Box>

                      {/* translator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
                        <Avatar
                          sx={{
                            bgcolor: colorForName(a.assignedToName),
                            width: 30,
                            height: 30,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(a.assignedToName)}
                        </Avatar>
                        <Typography sx={{ fontSize: 14, color: '#334155' }}>
                          {a.assignedToName || `#${a.assignedTo}`}
                        </Typography>
                      </Box>

                      {/* status */}
                      <Box sx={{ minWidth: 100 }}>
                        <StatusPill status={a.status} size="sm" />
                      </Box>

                      {/* submission / remark */}
                      <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                        {a.status === 'rejected' && a.reviewRemark ? (
                          <Box
                            sx={{
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '8px',
                              px: 1.25,
                              py: 0.75,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 13, color: '#b91c1c', fontStyle: 'italic' }}
                            >
                              “{a.reviewRemark}”
                            </Typography>
                          </Box>
                        ) : a.status === 'pending' ? (
                          <Typography sx={{ fontSize: 13.5, color: '#94a3b8' }}>
                            Awaiting submission from translator
                          </Typography>
                        ) : hasFile ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                          >
                            <Typography sx={{ fontSize: 13.5, color: '#475569' }}>
                              {submission.fileName || 'Translated file'}
                              {submission.fileSize != null && (
                                <span style={{ color: '#94a3b8' }}>
                                  {' '}
                                  · {formatBytes(submission.fileSize)}
                                </span>
                              )}
                            </Typography>
                            {submission.signedUrl && (
                              <Link
                                component="button"
                                type="button"
                                onClick={() =>
                                  downloadFile(submission.signedUrl, submission.fileName)
                                }
                                sx={{ fontSize: 13.5, fontWeight: 600, color: '#4f46e5' }}
                              >
                                Download
                              </Link>
                            )}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: '#94a3b8' }}>—</Typography>
                        )}
                      </Box>

                      {/* review actions — only on submitted rows */}
                      {isSubmitted && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                          {rowBusy ? (
                            <CircularProgress size={20} sx={{ color: '#6366f1', mx: 2 }} />
                          ) : (
                            <>
                              <Button
                                onClick={() => handleApprove(a)}
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircleOutline sx={{ fontSize: 18 }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  background: '#059669',
                                  boxShadow: 'none',
                                  '&:hover': { background: '#047857' },
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                onClick={() => {
                                  setRejectTarget(a);
                                  setRemark('');
                                  setRejectError('');
                                }}
                                variant="contained"
                                size="small"
                                startIcon={<HighlightOff sx={{ fontSize: 18 }} />}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  background: '#dc2626',
                                  boxShadow: 'none',
                                  '&:hover': { background: '#b91c1c' },
                                }}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Reject modal — remark required */}
      <Dialog
        open={!!rejectTarget}
        onClose={() => (reviewingId ? null : setRejectTarget(null))}
        PaperProps={{ sx: { borderRadius: '16px', width: '100%', maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 0.5 }}>
          Reject {rejectTarget?.languageName} translation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#64748b', mb: 2 }}>
            Send this back to {rejectTarget?.assignedToName || 'the translator'} with a note on what
            needs fixing. They'll re-upload and it returns for review.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="e.g. Row 12 mistranslated, please fix the amounts."
            InputProps={{ sx: { borderRadius: '10px' } }}
          />
          {rejectError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: '10px', py: 0.25 }}>
              {rejectError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setRejectTarget(null)}
            disabled={!!reviewingId}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#475569',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            disabled={!!reviewingId}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              background: '#dc2626',
              boxShadow: 'none',
              '&:hover': { background: '#b91c1c' },
            }}
          >
            {reviewingId ? 'Sending…' : 'Reject & Send Back'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel-task confirm dialog */}
      <Dialog
        open={cancelOpen}
        onClose={() => (cancelling ? null : setCancelOpen(false))}
        PaperProps={{ sx: { borderRadius: '16px', width: '100%', maxWidth: 440 } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: '#0f172a' }}
        >
          <WarningAmberRounded sx={{ color: '#dc2626' }} />
          Cancel this task?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#64748b' }}>
            Cancelling <b>{task?.taskName}</b> is permanent. Translators will no longer be able to
            submit to it, and any pending languages stop. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setCancelOpen(false)}
            disabled={cancelling}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#475569',
            }}
          >
            Keep Task
          </Button>
          <Button
            onClick={handleCancel}
            disabled={cancelling}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              background: '#dc2626',
              boxShadow: 'none',
              '&:hover': { background: '#b91c1c' },
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TaskDetail;
